import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Camera, Sparkles, Loader2, Sun, Clock, Hash, Copy } from "lucide-react";
import { toast } from "sonner";

export default function ContentPlanner() {
  const [cities, setCities] = useState([]);
  const [spots, setSpots] = useState([]);
  const [city, setCity] = useState("Marrakech");
  const [vibe, setVibe] = useState("authentic and vibrant");
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState(null);

  useEffect(() => {
    api.get("/data/cities").then((r) => setCities(r.data)).catch(() => {});
    api.get("/data/photo-spots").then((r) => setSpots(r.data)).catch(() => {});
  }, []);

  const generate = async () => {
    setLoading(true);
    setPlan(null);
    try {
      const { data } = await api.post("/content-planner", { city, vibe });
      setPlan(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not build plan");
    } finally {
      setLoading(false);
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  return (
    <div className="mx-auto max-w-7xl px-6 py-12 sm:px-10">
      <p className="overline text-accent">Instagram & content planner</p>
      <h1 className="mt-2 max-w-2xl font-display text-3xl font-bold tracking-tight sm:text-4xl">
        Shoot the perfect Morocco feed
      </h1>
      <p className="mt-2 max-w-2xl text-muted-foreground">
        Get AI-curated photo spots with the exact golden-hour timing, caption ideas and hashtags.
      </p>

      <div className="mt-8 flex flex-col gap-3 rounded-2xl border bg-card p-5 sm:flex-row sm:items-end">
        <div className="flex-1 space-y-2">
          <label className="text-sm font-medium">City</label>
          <Select value={city} onValueChange={setCity}>
            <SelectTrigger className="rounded-xl" data-testid="content-city"><SelectValue /></SelectTrigger>
            <SelectContent>
              {cities.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 space-y-2">
          <label className="text-sm font-medium">Vibe</label>
          <Input value={vibe} onChange={(e) => setVibe(e.target.value)} data-testid="content-vibe"
            placeholder="e.g. moody, minimal, colorful" className="rounded-xl" />
        </div>
        <Button onClick={generate} disabled={loading} className="rounded-full sm:w-auto" data-testid="generate-content">
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
          Generate plan
        </Button>
      </div>

      {loading && (
        <div className="mt-10 flex flex-col items-center justify-center rounded-2xl border bg-card py-20 text-center">
          <Loader2 className="h-10 w-10 animate-spin text-accent" />
          <p className="mt-4 font-medium">Scouting the best light in {city}…</p>
        </div>
      )}

      {plan && (
        <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2" data-testid="content-result">
          {(plan.spots || []).map((s, i) => (
            <motion.div
              key={`${s.name}-${i}`}
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              className="rounded-2xl border bg-card p-6"
            >
              <h3 className="font-display text-xl font-semibold">{s.name}</h3>
              <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{s.description}</p>
              <div className="mt-4 flex flex-wrap gap-2 text-xs">
                <span className="inline-flex items-center gap-1 rounded-full bg-secondary/10 px-3 py-1.5 text-secondary">
                  <Clock className="h-3 w-3" /> {s.best_time}
                </span>
                <span className="inline-flex items-center gap-1 rounded-full bg-accent/20 px-3 py-1.5">
                  <Sun className="h-3 w-3" /> {s.golden_hour}
                </span>
              </div>
              {s.lighting_notes && <p className="mt-3 text-sm text-muted-foreground">💡 {s.lighting_notes}</p>}
              {s.instagram_tip && <p className="mt-2 text-sm text-secondary">📸 {s.instagram_tip}</p>}
              {s.caption_idea && (
                <div className="mt-4 rounded-xl bg-muted p-3">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm italic">"{s.caption_idea}"</p>
                    <button onClick={() => copy(s.caption_idea)} className="shrink-0 text-muted-foreground hover:text-foreground">
                      <Copy className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              )}
              {s.hashtags?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {s.hashtags.map((h, hi) => (
                    <span key={hi} className="inline-flex items-center rounded-full bg-primary/10 px-2.5 py-0.5 text-xs text-primary">
                      <Hash className="h-2.5 w-2.5" />{h.replace(/^#/, "")}
                    </span>
                  ))}
                </div>
              )}
            </motion.div>
          ))}
        </div>
      )}

      {!plan && !loading && (
        <>
          <h2 className="mt-14 font-display text-2xl font-bold">Featured photo spots</h2>
          <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="featured-spots">
            {spots.map((s) => (
              <div key={s.id} className="group overflow-hidden rounded-2xl border bg-card transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_8px_32px_rgba(0,0,0,0.08)]">
                <div className="relative h-52 overflow-hidden">
                  <img src={s.image} alt={s.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <span className="glass absolute left-3 top-3 inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium">
                    <Clock className="h-3 w-3 text-secondary" /> {s.best_time}
                  </span>
                </div>
                <div className="p-5">
                  <h3 className="font-display text-lg font-semibold">{s.name}</h3>
                  <p className="text-sm text-muted-foreground">{s.city}</p>
                  <p className="mt-2 flex items-start gap-1.5 text-sm text-muted-foreground">
                    <Camera className="mt-0.5 h-3.5 w-3.5 shrink-0 text-secondary" /> {s.instagram_tip}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
